import {
    AlertColor,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    Link,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CalendarMonth, Cancel, CheckCircle, ChevronRight, LocationOn } from '@mui/icons-material';
import { FC, useCallback, useEffect, useState } from 'react';
import Notification from '../Notification';
import DrawerMenu from '../DrawerMenu';
import ListPage from '../../screens/Authenticated/CommonPage/ListPage';
import axiosInstance from '../../utils/axios';
import { URLS } from '../../utils/constants/urls';
import { PageProps } from '../../utils/constants/data';
import { useSelector } from 'react-redux';
import { filterDataforApi, pageNo, rowCount } from '../../utils/redux/reducer/filter-slice';
import dayjs from 'dayjs';

interface CheckInCheckoutProps {
    onAttendanceClick?: () => void;
}

interface LocationData {
    latitude: number;
    longitude: number;
    accuracy: number;
    timestamp: number;
}

interface CheckInStatus {
    checkIn?: string;
    checkOut?: string;
    latitude: number;
    longitude: number;
    mapUrl?: string;
}

const CheckInCheckout: FC<CheckInCheckoutProps> = () => {
    const index = useSelector(pageNo);
    const filter = useSelector(filterDataforApi);
    const pageSize = useSelector(rowCount);

    const theme = useTheme();
    const [isCheckedIn, setIsCheckedIn] = useState<boolean>(false);
    const [checkInStatus, setCheckInStatus] = useState<CheckInStatus | null>(null);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [isLoading, setIsLoading] = useState(false);
    const [attendanceDrawerOpen, setAttendanceDrawerOpen] = useState(false);
    const [pageResponse, setPageResponse] = useState<any[]>([]);

    const [locationPermission, setLocationPermission] = useState<'granted' | 'denied' | 'prompt'>('prompt');
    const [message, setMessage] = useState<{
        display: boolean;
        severity: AlertColor | null;
        message: string;
    }>({
        display: false,
        severity: null,
        message: '',
    });

    const handleCatch = (error: any) => {
        setMessage({
            display: true,
            severity: 'error',
            message: (error.response.data?.error_description ?? error.response.data.message) ?? error.message,
        });
    };

    useEffect(() => {
        const timeInterval = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        fetchCheckInStatus();
        checkLocationPermission();

        return () => clearInterval(timeInterval);
    }, []);

    const checkLocationPermission = async () => {
        if (!navigator.geolocation) {
            setLocationPermission('denied');
            return;
        }

        try {
            const permission = await navigator.permissions.query({name: 'geolocation' as PermissionName});
            setLocationPermission(permission.state);

            permission.onchange = () => {
                setLocationPermission(permission.state);
            };
        } catch (error) {
            console.error('Error checking location permission:', error);
            setLocationPermission('prompt');
        }
    };

    const getCurrentPosition = (): Promise<LocationData> => {
        return new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                position => {
                    resolve({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy,
                        timestamp: position.timestamp,
                    });
                },
                error => {
                    reject(error);
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 60000,
                },
            );
        });
    };

    const createGoogleMapsLink = (latitude: number, longitude: number): string => {
        return `https://www.google.com/maps?q=${latitude},${longitude}`;
    };

    const fetchCheckInStatus = async () => {
        try {
            setIsLoading(true);
            const {data} = await axiosInstance.get(URLS.getCheckInStatus || '/api/check-in-status');
            setCheckInStatus(data);
            setIsCheckedIn(Boolean(data?.checkIn && !data?.checkOut));
        } catch (error) {
            console.error('Error fetching check-in status:', error);
            setCheckInStatus(null);
            setIsCheckedIn(false);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchSessions = useCallback(
        async (filterData: PageProps) => {
            try {
                setIsLoading(true);
                const requestBody = filterData || {
                    page: index + 1,
                    size: pageSize,
                    filter: filter,
                    sort: ['checkIn:ASC']
                };
                const {data} = await axiosInstance.post(URLS.getAllSessionsForLoggedInUser, requestBody);
                const content = data?.content?.map((item: any) => {
                    return {
                        ...item,
                        checkIn: dayjs(item.checkIn).format('HH:mm ddd, MMM DD, YYYY'),
                        checkOut: item.checkOut ? dayjs(item.checkOut).format('HH:mm ddd, MMM DD, YYYY') : 'NA',
                        location:
                            <LocationOn
                                sx={{color: 'primary.main', cursor: 'pointer'}}
                                onClick={() => window.open(item.mapUrl, '_blank')}
                            />
                    };
                });
                setPageResponse({...data, content: content});
                setIsLoading(false);
            } catch (error: any) {
                handleCatch(error);
            } finally {
                setIsLoading(false);
            }
        },
        [index, pageSize, filter],
    );

    useEffect(() => {
        if (attendanceDrawerOpen) {
            (async () => {
                await fetchSessions(null as any);
            })();
        }
    }, [fetchSessions, attendanceDrawerOpen]);

    const handleCheckInOut = async () => {
        if (locationPermission === 'denied') {
            setMessage({
                display: true,
                severity: 'error',
                message: 'Location access is required for check-in. Please enable location in your browser settings.',
            });
            return;
        }

        if (locationPermission === 'prompt') {
            setMessage({
                display: true,
                severity: 'warning',
                message: 'Please allow location access when prompted by your browser.',
            });
        }

        setIsLoading(true);

        try {
            const position = await getCurrentPosition();
            const {data} = await axiosInstance.post(URLS.toggleSessions, {
                latitude: String(position.latitude),
                longitude: String(position.longitude),
                locationName: `${position.latitude}, ${position.longitude}`,
            });

            setCheckInStatus(data);
            setIsCheckedIn(Boolean(data?.checkIn && !data?.checkOut));

            setMessage({
                display: true,
                severity: 'success',
                message: isCheckedIn ? 'Successfully checked-out!' : 'Successfully checked-in!',
            });
        } catch (error: any) {
            setCheckInStatus(null);
            setIsCheckedIn(false);
            if (error.code === 1) {
                setLocationPermission('denied');
                setMessage({
                    display: true,
                    severity: 'error',
                    message: 'Location access denied. Please enable location in your browser settings to check in.',
                });
            } else if (error.code === 2) {
                setMessage({
                    display: true,
                    severity: 'error',
                    message: 'Unable to get your location. Please check your device settings.',
                });
            } else if (error.code === 3) {
                setMessage({
                    display: true,
                    severity: 'error',
                    message: 'Location request timed out. Please try again.',
                });
            } else {
                setMessage({
                    display: true,
                    severity: 'error',
                    message: error.response?.data?.message || 'Failed to process attendance. Please try again.',
                });
            }
        } finally {
            setIsLoading(false);
        }
    };


    const closeNotification = (value: boolean) => setMessage({...message, display: value});

    return (
        <>
            {message.display && (
                <Notification
                    isOpen={message.display}
                    message={message.message}
                    severity={message.severity as AlertColor}
                    closeNotification={closeNotification}
                    autoHideDuration={3000}
                />
            )}

            <Box sx={{ height: '100%', display: 'flex', flex: 1, minHeight: 0 }}>
                <Card
                    sx={{
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: 'none',
                    }}>
                    <CardContent
                      sx={{
                        p: 2,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        '&:last-child': { pb: 2 },
                      }}>
                        <Typography
                            fontWeight={700}
                            color="text.primary"
                            sx={{
                                fontSize: { xs: '2rem', sm: '2.25rem' },
                                lineHeight: 1.1,
                                fontVariantNumeric: 'tabular-nums',
                            }}>
                            {dayjs(currentTime).format('HH:mm')}
                        </Typography>
                        <Typography
                            color="text.secondary"
                            sx={{
                                mt: 0.5,
                                fontSize: '0.875rem',
                                fontWeight: 500,
                            }}>
                            {dayjs(currentTime).format('ddd, D MMM YYYY')}
                        </Typography>

                        <Button
                            variant="contained"
                            onClick={handleCheckInOut}
                            disabled={isLoading}
                            startIcon={
                                isLoading ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : isCheckedIn ? (
                                    <Cancel />
                                ) : (
                                    <CheckCircle />
                                )
                            }
                            sx={{
                                mt: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                minHeight: 40,
                                borderRadius: 1.5,
                            }}>
                            {isLoading
                                ? isCheckedIn
                                    ? 'Checking out...'
                                    : 'Checking in...'
                                : isCheckedIn
                                  ? 'Check Out'
                                  : 'Check In'}
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<CalendarMonth />}
                            onClick={() => setAttendanceDrawerOpen(true)}
                            sx={{
                                mt: 1.25,
                                textTransform: 'none',
                                fontWeight: 600,
                                minHeight: 40,
                                borderRadius: 1.5,
                            }}>
                            View sessions
                        </Button>

                        <Divider sx={{ my: 2 }} />

                        <Link
                            href="#"
                            underline="none"
                            onClick={e => {
                                e.preventDefault();
                                if (checkInStatus?.latitude && checkInStatus?.longitude) {
                                    window.open(
                                        createGoogleMapsLink(checkInStatus.latitude, checkInStatus.longitude),
                                        '_blank',
                                    );
                                } else {
                                    getCurrentPosition()
                                        .then(position => {
                                            window.open(createGoogleMapsLink(position.latitude, position.longitude), '_blank');
                                        })
                                        .catch(() => {
                                            setMessage({
                                                display: true,
                                                severity: 'error',
                                                message: 'Unable to get location. Please check your location settings.',
                                            });
                                        });
                                }
                            }}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                color: 'text.primary',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                cursor: 'pointer',
                                '&:hover': { color: 'primary.main' },
                            }}>
                            View Location
                            <ChevronRight fontSize="small" />
                        </Link>

                        <Box sx={{ mt: 1.5 }}>
                            <Chip
                                size="small"
                                icon={<LocationOn />}
                                label={
                                    locationPermission === 'granted'
                                        ? 'Location Enabled'
                                        : locationPermission === 'denied'
                                          ? 'Location Disabled'
                                          : 'Location Pending'
                                }
                                sx={{
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    bgcolor:
                                        locationPermission === 'granted'
                                            ? alpha(theme.palette.success.main, 0.12)
                                            : locationPermission === 'denied'
                                              ? alpha(theme.palette.error.main, 0.12)
                                              : alpha(theme.palette.warning.main, 0.12),
                                    color:
                                        locationPermission === 'granted'
                                            ? theme.palette.success.dark
                                            : locationPermission === 'denied'
                                              ? theme.palette.error.dark
                                              : theme.palette.warning.dark,
                                    '& .MuiChip-icon': {
                                        color:
                                            locationPermission === 'granted'
                                                ? theme.palette.success.main
                                                : locationPermission === 'denied'
                                                  ? theme.palette.error.main
                                                  : theme.palette.warning.main,
                                    },
                                }}
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            <DrawerMenu
                width="50%"
                open={attendanceDrawerOpen}
                handleClose={() => setAttendanceDrawerOpen(false)}
                closeButton={true}
                title="Sessions">
                <ListPage
                    pageConfig={{
                        title: '',
                        listApi: URLS.getAllSessionsForLoggedInUser,
                        tableColumn: [
                            {field: 'checkIn', headerName: 'Check-In', width: 150},
                            {field: 'checkOut', headerName: 'Check-Out', width: 150},
                            {field: 'location', headerName: 'Location', width: 150,},
                        ],
                        hideActionText: true
                    }}
                    pageResponse={pageResponse}
                    getList={fetchSessions}
                />
            </DrawerMenu>
        </>
    );
};

export default CheckInCheckout;
